import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import VButton from "components/ui/VButton";
import useAsync from "hooks/useAsync";
import { useRef, useState } from "react";
import currency from "utilities/currency";
import { showDanger, showSuccess } from "utilities/misc";
import { post } from "utilities/network";

const CreditUser: React.FC = () => {
  const [amount, setAmount] = useState("");
  const [userName, setUserName] = useState("");
  const [creditUserType, setType] = useState<any>("earning");
  const [reason, setReason] = useState("");
  const submitting = useRef(false);

  const { execute: creditUser, status } = useAsync(async () => {
    const response = await post(`admin/transactions/creditUser`, {
      amount,
      userName,
      creditUserType,
      reason,
    });
    if (response?.status === "Success" || response?.data?.success) {
      showSuccess(
        response?.message ||
          response?.data?.message ||
          "Credit request submitted for approval",
      );
      // Clear the submitted values before the button becomes available again.
      setUserName("");
      setAmount("");
      setReason("");
    }
    return response;
  }, false);

  const handleSubmit = async () => {
    if (submitting.current) return;
    if (!userName || userName.length < 3)
      return showDanger("Enter valid username");
    if (!amount || Number(amount) < 1)
      return showDanger("Amount cannot be less than 0");
    if (!reason.trim()) return showDanger("Reason is required");
    // Lock immediately: React's pending state is applied on the next render.
    submitting.current = true;
    try {
      await creditUser();
    } finally {
      submitting.current = false;
    }
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <h2 className="text-xl">Credit User</h2>
        <div className="flex gap-8 mt-14">
          <div className="w-8/12">
            <div className="relative mb-5 md:w-10/12">
              <label htmlFor="credit-user-type">Type</label>
              <select
                id="credit-user-type"
                disabled={status === "pending"}
                className="w-full py-3 pl-8 mt-1 border-gray-400 rounded"
                value={creditUserType}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="earning">Earning</option>
                <option value="refund">Refund</option>
              </select>
            </div>

            <div className="flex gap-5">
              <div className="relative w-5/12">
                <label htmlFor="credit-user-name">Username</label>
                <input
                  id="credit-user-name"
                  disabled={status === "pending"}
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="w-full py-3 pl-8 mt-1 border-gray-400 rounded"
                />
                <span className="absolute top-[41px] left-4">@</span>
              </div>
              <div className="relative w-5/12">
                <label htmlFor="credit-user-amount">Amount</label>
                <input
                  id="credit-user-amount"
                  disabled={status === "pending"}
                  type="number"
                  className="w-full py-3 pl-8 mt-1 border-gray-400 rounded"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <span className="font-bold absolute top-[41px] left-4">
                  {currency("ngn")}
                </span>
              </div>
            </div>

            <div className="relative mt-5 md:w-10/12">
              <label htmlFor="credit-user-reason">Reason</label>
              <textarea
                id="credit-user-reason"
                disabled={status === "pending"}
                className="w-full min-h-[100px] py-3 px-4 mt-1 border-gray-400 rounded"
                placeholder="Why should this account be credited?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                A second authorized admin must approve this request before the balance changes.
              </p>
            </div>

            <div className="text-center">
              <VButton
                status={status}
                onClick={handleSubmit}
                className="w-4/12 px-20 py-3 mt-8 font-light text-white bg-black rounded-lg"
              >
                Credit
              </VButton>
            </div>
          </div>
        </div>
      </Main>
    </Container>
  );
};
export default CreditUser;
